import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, checkBoxHelper, getControl, getGroup } from '@shared/helpers/form.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { toNum } from '@shared/helpers/math.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { formPaths } from '@shared/constants/form-paths.const';

export const getSchedaCoopAgricoleGroup = (form: FormGroup): FormGroup =>
    getGroup(form, formPaths.schedaCoopAgricole);

export const getSchedaCoopAgricoleControl = (form: FormGroup, path: string) =>
    getControl(form, `${formPaths.schedaCoopAgricole}.${path}`);

/** Wrapper su checkBoxHelper con subFormPath fisso a schedaCoopAgricole */
export const schedaCoopAgricoleCkHelper = (
    form: FormGroup,
    targetPath: string,
    ckPath: string,
    ckTargetValue: number,
    suppCondition = true,
    errorStatus?: string
): void => checkBoxHelper(form, targetPath, ckPath, ckTargetValue, formPaths.schedaCoopAgricole, suppCondition, errorStatus);

/** Campo required quando condition è vera */
export const requiredBy = (form: FormGroup, targetPath: string, condition: boolean): void => {
    const control = getSchedaCoopAgricoleControl(form, targetPath);
    const errors = control.errors ?? {};
    addRemoveError(isNilOrEmpty(control.value) && condition, errors, targetPath, { status: REQUIRED_STATUS });
    control.setErrors(isEqual(errors, {}) ? null : errors);
};

/** Required quando my_ckTerreniAQualsiasiTitolo === 1 e suppCondition vera */
export const ckTerreniHelper = (form: FormGroup, targetPath: string, suppCondition: boolean): void => {
    schedaCoopAgricoleCkHelper(form, targetPath, 'my_ckTerreniAQualsiasiTitolo', 1, suppCondition);
};

/** True se la scheda alte coop richiede la scheda agricole per il ckAltrePath dato */
export const altreCoopRequired = (form: FormGroup, ckAltrePath: string): boolean => {
    const schedaAltreCoopGroup = getGroup(form, formPaths.schedaAltreCoop);
    const ckAltreTarget = toNum(getControl(schedaAltreCoopGroup, ckAltrePath).value);
    const my_ScAltreCoopObbl = toNum(getControl(schedaAltreCoopGroup, 'my_ScAltreCoopObbl').value);
    return my_ScAltreCoopObbl === 1 && ckAltreTarget === 1;
};

/** True se my_ScAgricoleObbl === 1 e my_TipoSezEnte === tipoSezTarget */
export const scAgricoleObblRequired = (form: FormGroup, tipoSezTarget: number): boolean => {
    const scAgricoleObbl = toNum(getSchedaCoopAgricoleControl(form, 'my_ScAgricoleObbl').value);
    const my_TipoSezEnte = toNum(getControl(form, `${formPaths.datiIdentificativi}.my_TipoSezEnte`).value);
    return scAgricoleObbl === 1 && my_TipoSezEnte === tipoSezTarget;
};

/** True se la sezione lavoro agricolo è obbligatoria */
export const lavoroAgricoloRequired = (form: FormGroup): boolean => {
    const altreCoopReq = altreCoopRequired(form, 'my_CkAltreCoopAllegaSchedaAgricole');
    const ckAllegaAgricole = toNum(
        getControl(getGroup(form, formPaths.schedaAltreCoop), 'my_CkAltreCoopAllegaSchedaAgricole').value
    );
    return altreCoopReq || (scAgricoleObblRequired(form, 6) && ckAllegaAgricole !== 1);
};

/** True se la sezione prodotti agricoli è obbligatoria */
export const prodottiAgricoliRequired = (form: FormGroup): boolean =>
    altreCoopRequired(form, 'my_CkAltreCoopAllegaSchedaAgricoleConferimento') || scAgricoleObblRequired(form, 14);

/** Valida che almeno uno dei tre campi stabilimento sia selezionato quando my_CkDispStabilimento === 1 */
export const stabilimentoHelper = (form: FormGroup): void => {
    const schedaGroup = getSchedaCoopAgricoleGroup(form);
    const proprControl = getControl(schedaGroup, 'my_CkStabilimentoPropr');
    const affitControl = getControl(schedaGroup, 'my_CkStabilimentoAffit');
    const altroTitControl = getControl(schedaGroup, 'my_CkStabilimentoAltroTit');
    const ckDispStabilimento: string = getControl(schedaGroup, 'my_CkDispStabilimento').value;
    const my_TipoSezEnte = toNum(getControl(form, `${formPaths.datiIdentificativi}.my_TipoSezEnte`).value);
    const my_CkAltreCoopAllegaSchedaAgricoleConferimento = toNum(
        getControl(getGroup(form, formPaths.schedaAltreCoop), 'my_CkAltreCoopAllegaSchedaAgricoleConferimento').value
    );
    const proprErrors = proprControl.errors ?? {};
    const affitErrors = affitControl.errors ?? {};
    const altroTitErrors = altroTitControl.errors ?? {};

    const noneChecked =
        toNum(proprControl.value) !== 1 && toNum(affitControl.value) !== 1 && toNum(altroTitControl.value) !== 1;
    const hasError =
        noneChecked &&
        ckDispStabilimento === '1' &&
        (scAgricoleObblRequired(form, 14) || (my_TipoSezEnte === 10 && my_CkAltreCoopAllegaSchedaAgricoleConferimento === 1));

    addRemoveError(hasError, proprErrors, 'my_CkStabilimentoPropr', { status: REQUIRED_STATUS });
    addRemoveError(hasError, affitErrors, 'my_CkStabilimentoPropr', { status: REQUIRED_STATUS });
    addRemoveError(hasError, altroTitErrors, 'my_CkStabilimentoPropr', { status: REQUIRED_STATUS });

    proprControl.setErrors(isEqual(proprErrors, {}) ? null : proprErrors);
    affitControl.setErrors(isEqual(affitErrors, {}) ? null : affitErrors);
    altroTitControl.setErrors(isEqual(altroTitErrors, {}) ? null : altroTitErrors);
};
